import { ApiRequestError, getApiErrorMessage } from '@/lib/api';
import { DEFAULT_TIMEZONE, isUtc, zoneAbbreviation } from '@/lib/dateFormat';
import { DOCK_READY_BASIS_LABEL, UNBOUNDED_DOCK_READY_BASES } from '@/lib/copy';
import type { RotationSchedulePayload } from '@/hooks/useWebSocket';
import type { ReplacementView, RotationActionRow } from './coverageTypes';
import { utcInputValueToIso } from './missionDetailShared';

/**
 * READING THE COVERAGE WIRE, AND WRITING IT BACK OUT AS AN OPERATOR READS IT.
 *
 * Extracted verbatim from `MissionCoveragePanel.tsx`. Two jobs that belong together because the
 * second only ever runs on the output of the first: normalising the two shapes a forecast arrives
 * in, then rendering their values with the one rule this whole surface obeys — an absent value is
 * displayed as an absence, never as a zero.
 */

export function str(v: unknown): string | null {
  return typeof v === 'string' && v.length > 0 ? v : null;
}

export function obj(v: unknown): Record<string, unknown> | null {
  return typeof v === 'object' && v !== null && !Array.isArray(v)
    ? (v as Record<string, unknown>)
    : null;
}

export function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** The relief half, from either source, or null when the payload carries none. */
export function reliefFrom(raw: unknown): ReplacementView['relief'] {
  const r = obj(raw);
  if (!r) return null;
  const rows = Array.isArray(r.rejected) ? r.rejected : [];
  return {
    droneId: str(r.drone_id),
    callsign: str(r.callsign),
    rotationOrder: num(r.rotation_order),
    dockId: str(r.dock_id),
    unavailableReason: str(r.unavailable_reason),
    rejected: rows
      .map((row) => obj(row))
      .filter((row): row is Record<string, unknown> => row !== null)
      .filter((row) => row.rejection !== 'holding_the_station')
      .map((row) => ({
        callsign: str(row.callsign),
        rejection: str(row.rejection) ?? 'unknown',
        reasons: Array.isArray(row.reasons) ? row.reasons.filter((x): x is string => typeof x === 'string') : [],
      })),
  };
}

/**
 * The schedule half of a frame. The RELIEF is applied separately by `replacement` in the panel,
 * because the two arrive on different clocks and a frame carrying one must not erase the other —
 * which is the whole reason the server broadcasts them as independent halves.
 */
export function fromFrame(schedule: RotationSchedulePayload): ReplacementView {
  return {
    decision: schedule.decision,
    reason: schedule.reason,
    launchAt: schedule.launch_at,
    outgoingMustLeaveAt: schedule.outgoing_must_leave_at,
    commitDeadline: schedule.commit_deadline,
    commitState: schedule.commit_state,
    marginSource: schedule.margin_source,
    marginRejectedReason: schedule.margin_rejected_reason,
    outgoingCallsign: schedule.outgoing_callsign,
    // Filled in by `replacement`, from whichever relief source is fresher. `schedule.relief` does
    // not exist on the wire and never did — see `RotationReliefPayload`.
    relief: null,
    breakdown: schedule.breakdown,
    // A SIBLING of `breakdown`, never a key inside it. `breakdown` is `ReplacementTimingBreakdown`
    // verbatim from the pure estimator, which has no `resolved` key and never will — it knows
    // nothing about where its arguments came from. Reading it from there produced `null` on every
    // live frame, so five rows of this disclosure flipped from real numbers to
    // "Unknown — no energy profile for this airframe" the moment a socket frame replaced a poll.
    resolved: schedule.resolved,
  };
}

export function fromRow(row: RotationActionRow | null): ReplacementView | null {
  if (!row) return null;
  const inputs = row.inputs ?? {};
  const source = str(inputs.margin_source);
  return {
    decision: inputs.decision === 'launch_now' ? 'launch_now' : 'scheduled',
    reason: str(inputs.reason),
    launchAt: str(inputs.launch_at) ?? row.planned_at,
    outgoingMustLeaveAt: str(inputs.outgoing_must_leave_at),
    commitDeadline: str(inputs.commit_deadline),
    commitState: row.commit_state === 'committed' || row.commit_state === 'tentative'
      ? row.commit_state
      : null,
    marginSource: source === 'dji' || source === 'derived' ? source : null,
    marginRejectedReason: str(inputs.dji_rejected_reason),
    outgoingCallsign: str(inputs.outgoing_callsign),
    relief: reliefFrom(inputs.relief),
    breakdown: obj(inputs.breakdown),
    resolved: obj(inputs.resolved),
  };
}

/** Seconds as an operator reads them. `null` becomes an explicit absence, never a zero. */
export function secs(v: unknown): string {
  const n = num(v);
  return n === null ? 'Unknown' : `${Math.round(n)}s`;
}

export function metres(v: unknown): string {
  const n = num(v);
  return n === null ? 'Unknown' : `${Math.round(n)} m`;
}

/**
 * How soon a relief could become launchable, and on what evidence (stage 5 W1).
 *
 * THE BASIS DECIDES WHETHER A NUMBER IS SHOWN AT ALL. Five of the eight report zero seconds
 * because there is no ETA to give, not because a relief is ready now. Rendering those as "0s"
 * would tell an operator that a dock with an empty magazine can launch this second. Same
 * discipline as the battery row above: an absence is displayed as an absence.
 *
 * THE SWAP NUMBER IS A CEILING, NOT A COUNTDOWN, and it says so. The platform cannot recover when
 * a swap started (`docks.status` is last-value-wins), so the same figure is reported on every
 * sweep - while `Flight left` above it genuinely decrements. Without "up to", one frozen number
 * beside a moving one reads as a stuck dock.
 *
 * WORTH KNOWING WHEN READING "Ready": an ENTIRELY empty magazine used to derive as ready and reach
 * this row saying so. #548 fixed that upstream, in `deriveStatusEnum`, and it now renders here as
 * "No charged battery, wait unknown" - the closest phrase the basis vocabulary has, though it
 * describes packs present and flat rather than a magazine holding nothing.
 *
 * The residual case, named in full in `src/services/dock-time-to-ready.ts` rather than restated
 * here: an empty magazine behind a bay that is NOT CLOSED still reads "Ready". `tray_open` is a
 * settled bay, not a transient, and `fireBayLeftOpenAlert` exists because one can stay open for a
 * whole sortie - so that window is a flight, not a few seconds of travel.
 */
export function reliefReady(seconds: unknown, basis: unknown): string {
  const key = typeof basis === 'string' ? basis : '';
  const label = DOCK_READY_BASIS_LABEL[key as keyof typeof DOCK_READY_BASIS_LABEL];
  // A basis this build does not recognise, or none at all (a forecast row written before this
  // shipped, until the next sweep rewrites it).
  if (label === undefined) return DOCK_READY_BASIS_LABEL.unknown;
  // THE STATE LEADS, then the number. Reading order puts the fact an operator acts on first, and
  // it is the only arrangement that leaves EMERGENCY shouting rather than lower-cased mid-phrase.
  if (key === 'ready' || key === 'awaiting_connection' || key === 'unknown') return label;
  if (UNBOUNDED_DOCK_READY_BASES.has(key)) return `${label}, wait unknown`;
  return `${label}, up to ${secs(seconds)}`;
}

/** Does this basis mean no relief is coming at all? Those are amber, not body text. */
export function reliefReadyIsBad(basis: unknown): boolean {
  return typeof basis === 'string'
    && basis !== 'unknown'
    && UNBOUNDED_DOCK_READY_BASES.has(basis);
}

/** `hour12: false` renders midnight as 24:00 — `h23` is the cycle that gives 00:00. */
const ZONED_STAMP: Intl.DateTimeFormatOptions = {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
};

/** 'Z' for UTC (byte-identical to the hardcoded suffix this whole panel used to
 *  append), or the zone's short name otherwise — appended straight onto a bare
 *  HH:MM/date stamp. Kept local rather than exported from dateFormat.ts so the
 *  space-constrained collapsed chip below stays exactly as short as it was for
 *  a UTC-default org. */
function zoneSuffix(value: string | number, timezone: string): string {
  return isUtc(timezone) ? 'Z' : ` ${zoneAbbreviation(value, timezone)}`;
}

/**
 * One instant, in the org's display timezone, as HH:MM plus the zone's short name — no date.
 *
 * For the COLLAPSED line only, which is a live surface: a replacement due in twenty minutes does
 * not need "Sep 16," in front of it, and the full stamp made the chip ~45 characters. On a 360px
 * row the header's `shrink-0` chips are laid out before the `flex-1 truncate` summary, so every
 * character here is taken directly out of "· HELD BY TALON-1" — the half of the line that answers
 * "and by whom". The expanded rows keep the full stamp, where the date is worth its width.
 */
export function utcClock(iso: string | number, timezone: string = DEFAULT_TIMEZONE): string {
  const ms = typeof iso === 'number' ? iso : Date.parse(iso);
  if (!Number.isFinite(ms)) return 'UNKNOWN TIME';
  const base = new Date(ms).toLocaleString('en-US', {
    timeZone: timezone, hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  });
  return `${base}${zoneSuffix(ms, timezone)}`;
}

/** One instant in the org's display timezone, stamped, or a named refusal — never a silent
 *  "Invalid Date". */
export function utcStamp(iso: string | number, timezone: string = DEFAULT_TIMEZONE): string {
  const ms = typeof iso === 'number' ? iso : Date.parse(iso);
  if (!Number.isFinite(ms)) return 'UNKNOWN TIME';
  const base = new Date(ms).toLocaleString('en-US', { ...ZONED_STAMP, timeZone: timezone });
  return `${base}${zoneSuffix(ms, timezone)}`;
}

/** Same instant in the viewer's own browser zone, because the org-timezone box above it is
 *  easy to misread as the viewer's own clock when the two differ. */
const LOCAL_STAMP: Intl.DateTimeFormatOptions = {
  month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit', hourCycle: 'h23',
  timeZoneName: 'short',
};

/**
 * The numbers behind "why 14:32", in the order the arithmetic reaches them.
 *
 * EVERY ROW NAMES ITS PROVENANCE where it has one. §6 says a forecast carries its inputs and §6.1
 * adds that it carries their source; "30 min" means three different things depending on whether it
 * came from a calibrated sample, a hand-typed override, or 75% of a manufacturer figure, and an
 * operator deciding whether to trust the time is entitled to know which.
 *
 * An absent value is rendered as UNKNOWN and never as `0`. That is the same rule the server obeys
 * end to end — a missing battery is not a flat one — and it is the one place a display can quietly
 * undo it.
 */
export function breakdownRows(view: ReplacementView, timezone: string): Array<{ label: string; value: string; bad?: boolean }> {
  const b = view.breakdown ?? {};
  const r = view.resolved ?? {};
  const rows: Array<{ label: string; value: string; bad?: boolean }> = [];

  rows.push({ label: 'Flight left', value: secs(b.remainingFlightSeconds) });
  rows.push({ label: 'Return costs', value: `${secs(b.returnSeconds)} · ${metres(b.returnDistanceM)}` });
  rows.push({
    label: 'Relief transit',
    value: `${secs(b.replacementTransitSeconds)} · ${metres(b.outboundDistanceM)}`,
  });
  rows.push({ label: 'Bay + climb', value: secs(b.dockLaunchLatencySeconds) });
  // NOT A TERM IN THE ARITHMETIC ABOVE, and the label says so rather than sitting in the run of
  // durations as though it were one. The launch time does not include this wait - see
  // `soonestReliefReadiness` on the server for why subtracting it would have made a rotation fail
  // more often. It answers the question the rows above leave open when no relief is named, which
  // is the ordinary state of a 24/7 rotation: is one coming, and when.
  //
  // Conditional, like `Commit by` and `Relief from`: a forecast row written before this shipped
  // carries neither field, and an unexplained "Not reported" in the one row whose whole design
  // rests on an absence meaning something is worse than no row at all.
  if (r.relief_available_basis !== undefined) {
    rows.push({
      label: 'Relief ready',
      value: reliefReady(r.relief_available_in_seconds, r.relief_available_basis),
      bad: reliefReadyIsBad(r.relief_available_basis),
    });
  }
  rows.push({ label: 'Safety margin', value: secs(b.marginSeconds) });

  const battery = num(r.battery_percent);
  const reserve = num(r.reserve_battery_percent);
  rows.push({
    label: 'Battery',
    value: battery === null
      ? 'Unknown'
      : `${battery}%${reserve === null ? '' : ` · reserve ${reserve}%`}`,
  });

  // The profile figure and the one the estimator actually ran on are DIFFERENT NUMBERS whenever a
  // drain rate was observed, and the difference is the whole of §6's second row. Showing only one
  // of them would hide where the observed drain went.
  const profile = num(r.endurance_minutes_at_full);
  const effective = num(r.effective_endurance_minutes);
  rows.push({
    label: 'Endurance',
    value: profile === null
      ? 'Unknown — no energy profile for this airframe'
      : effective !== null && Math.abs(effective - profile) > 0.05
        ? `${effective.toFixed(0)} min used · ${profile.toFixed(0)} min ${String(r.endurance_source ?? '')}`
        : `${profile.toFixed(0)} min · ${String(r.endurance_source ?? '')}`,
  });

  const drain = num(r.drain_percent_per_minute);
  rows.push({
    label: 'Observed drain',
    value: drain === null
      ? `Not measured · ${String(r.drain_outcome ?? 'unknown')}`
      : `${drain.toFixed(2)} %/min`,
  });

  const cruise = num(r.cruise_speed_ms);
  rows.push({
    label: 'Cruise speed',
    value: cruise === null
      ? 'Unknown — no calibrated speed and no planned speed'
      : `${cruise} m/s · ${String(r.cruise_source ?? '')}`,
  });

  if (view.commitDeadline) {
    rows.push({ label: 'Commit by', value: utcStamp(view.commitDeadline, timezone) });
  }

  // Labelled as the WORST CASE, because it is not a chosen aircraft's dock — the planner selects
  // no candidate, so it plans against the relief dock that costs the most transit.
  const dockName = str(r.replacement_dock_name);
  if (dockName) rows.push({ label: 'Relief from', value: `${dockName} · worst case` });

  // #703. THE RESERVE IS NO LONGER ALWAYS THE NUMBER THE OPERATOR TYPED. It is the higher of
  // their `battery_normal_pct` and the percentage at which this airframe's own low-battery RTH
  // would take it - because when DJI's RTH fires it takes the flight controller, our return
  // wayline is refused after dispatch, and the sortie ends on a GPS-home landing with no tray and
  // no tag. So when the two differ, BOTH are shown: an operator who asked for 20% and sees a
  // rotation planned around 26% is owed the reason on the same panel.
  const configuredReserve = num(r.configured_reserve_battery_percent);
  const effectiveReserve = num(r.reserve_battery_percent);
  if (
    configuredReserve !== null && effectiveReserve !== null
    && effectiveReserve > configuredReserve
  ) {
    const takeover = num(r.dji_takeover_battery_percent);
    const reported = r.dji_takeover_source === 'dji_reported';
    // TWO SHORT ROWS, NOT ONE LONG ONE. `ReadRow` renders its value `truncate` in a `w-28`-labelled
    // flex row inside a two-column drawer, so the single 121-character sentence this started as
    // was cut off at every realistic width - and the clause it lost first was the "(assumed)"
    // qualifier, which is the part that decides how much the number beside it is worth. Two rows
    // of about twenty characters each fit a 320px COP column.
    //
    // "RETURN AT", NOT "LAND AT", and the collision is why: the settings drawer already labels
    // `battery_normal_pct` "Land at" and the summary line above it uses the same two words for
    // the CRITICAL threshold. `CoverageRulesStep` had already made this call for the field itself
    // ("BRING HOME AT", NOT "LAND AT") on the ground that the aircraft STARTS returning at this
    // figure and touches down below it; the same reasoning applies here.
    rows.push({
      label: 'Return at',
      value: `${effectiveReserve}% · you set ${configuredReserve}%`,
    });
    rows.push({
      label: 'Raised by',
      value: `aircraft RTH at ${takeover ?? '—'}%${reported ? '' : ' · assumed'}`,
    });
  }

  const dji = obj(b.djiMarginsUsed);
  if (dji) {
    // DJI's figures AS REPORTED, not as converted. `remainingFlightTimeS` counts down to a flat
    // pack while `Flight left` above counts down to our reserve, so printing one under the other
    // without saying so would look like an inconsistency in the arithmetic.
    rows.push({
      label: 'DJI reported',
      value: `${secs(dji.remainingFlightTimeS)} to flat · ${secs(dji.timeNeededToGoHomeS)} home · `
        + `${num(dji.batteryPercentNeededToGoHome) ?? '—'}% to get home`,
    });
  }

  return rows;
}

/**
 * The window, or why it cannot be read.
 *
 * Unparseable timestamps are NOT folded into "UNTIL STOPPED": that would render a bounded
 * commitment as an open-ended one, which is the opposite of the truth and the one wrong
 * answer an operator would act on.
 */
export function formatWindow(start: string | null, end: string | null, timezone: string = DEFAULT_TIMEZONE): { text: string; bad: boolean } {
  if (start === null && end === null) return { text: 'UNTIL STOPPED', bad: false };
  const from = start === null ? NaN : Date.parse(start);
  const to = end === null ? NaN : Date.parse(end);
  if (!Number.isFinite(from) || !Number.isFinite(to)) return { text: 'INVALID WINDOW', bad: true };
  const opts = { ...ZONED_STAMP, timeZone: timezone };
  const fromText = `${new Date(from).toLocaleString('en-US', opts)}${zoneSuffix(from, timezone)}`;
  const toText = `${new Date(to).toLocaleString('en-US', opts)}${zoneSuffix(to, timezone)}`;
  return {
    text: `${fromText} → ${toText}`,
    bad: false,
  };
}

/** Six decimals, always — the coordinate convention everywhere else on the COP. */
export function formatCoord(value: number): string {
  return value.toFixed(6);
}

/** Metres between two coordinates. Enough for "has the route moved away from the target". */
export function haversineM(aLat: number, aLon: number, bLat: number, bLon: number): number {
  const R = 6371000;
  const dLat = ((bLat - aLat) * Math.PI) / 180;
  const dLon = ((bLon - aLon) * Math.PI) / 180;
  const h = Math.sin(dLat / 2) ** 2
    + Math.cos((aLat * Math.PI) / 180) * Math.cos((bLat * Math.PI) / 180) * Math.sin(dLon / 2) ** 2;
  return R * 2 * Math.atan2(Math.sqrt(h), Math.sqrt(1 - h));
}

/** The local-time reading of what is typed in a UTC box, or null when the box is mid-edit. */
export function localEcho(value: string): string | null {
  const iso = utcInputValueToIso(value);
  if (iso === null) return null;
  return new Date(iso).toLocaleString(undefined, LOCAL_STAMP);
}

/** Seconds as an operator reads a duration: `9s`, `4m 20s`, `2h 05m`. */
export function formatDuration(totalSeconds: number): string {
  const s = Math.round(totalSeconds);
  if (s < 60) return `${s}s`;
  if (s < 3600) return `${Math.floor(s / 60)}m ${String(s % 60).padStart(2, '0')}s`;
  return `${Math.floor(s / 3600)}h ${String(Math.floor((s % 3600) / 60)).padStart(2, '0')}m`;
}

/**
 * The server's sentence — including the FIELD-level detail a Zod refusal carries.
 *
 * `validate()` answers `{ error: 'Validation failed', details: [...] }`, so
 * `getApiErrorMessage` alone renders a toast that names nothing at all. The details are
 * exactly the part an operator can act on.
 */
export function describeApiError(err: unknown, fallback: string): string {
  if (err instanceof ApiRequestError && err.details && err.details.length > 0) {
    return err.details.map((d) => `${d.field}: ${d.message}`).join(' · ');
  }
  return getApiErrorMessage(err, fallback);
}
